/* eslint-disable @typescript-eslint/no-unused-vars */
import { useCallback } from 'react';
import fp from 'lodash/fp';

import crypto from 'crypto-js';
import { useClient } from './useClient';
import { useSession } from 'store/session';

const HOST: string | undefined = process.env.REACT_APP_API_V3?.replace(
  /https:\/\/|http:\/\/|\/v3/g,
  '',
);

/**
 * Get the md5 sequence for an user's organization .
 * @param organization - organization id that will be parsed to its uuid equivalent.
 * @returns {string}
 */
const getOrgSeq = (organization: number): string =>
  `${HOST}/orgs/${organization}`;

/**
 * Get the md5 sequence for the fake organization's fake folder.
 * @param user - user id related to the legacy fake org.
 * @returns {string}
 */
const getFakeOrgFakeFolderSeq = (user: number): string =>
  `${HOST}/users/${user}/folder`;

/**
 * Get the md5 sequence for a fake organization's folder.
 * @param user - user id related to the legacy fake org.
 * @param folder - folder id related to the legacy fake org.
 * @return {string}
 */
const getFakeOrgFolderSeq = (user: number, folder: number): string =>
  `${HOST}/users/${user}/folders/${folder}`;

/**
 * Get the md5 sequence for an organization's fake folder.
 * @param organization - organization id related to the fake folder.
 * @returns {string}
 */
const getOrgFakeFolderSeq = (organization: number): string =>
  `${HOST}/orgs/${organization}/folder`;

/**
 * Get the md5 sequence for and organization's folder.
 * @param organization - organization id related to the folder.
 * @param folder - folder id related to the organization.
 * @returns {string}
 */
const getOrgFolderSeq = (organization: number, folder: number): string =>
  `${HOST}/orgs/${organization}/folders/${folder}`;

/**
 * Generates an UUID string based on the CINCEL API URL and the legacy org id.
 * @param {string} payload - string to digest.
 * @returns {string}
 */
const generateUUID = (payload: string): string => {
  const hash = crypto.MD5(payload).toString(crypto.enc.Hex);

  const firstPart = hash.slice(0, 8);
  const secondPart = hash.slice(8, 12);
  const thirdPart = hash.slice(12, 16);
  const fourthPart = hash.slice(16, 20);
  const fifthPart = hash.slice(20, 32);

  return `${firstPart}-${secondPart}-${thirdPart}-${fourthPart}-${fifthPart}`;
};

export const useLegacyOrganizations = () => {
  const v2 = useClient({ url: process.env.REACT_APP_API });

  const { setOrgsToTeamsMap } = useSession();

  const fn = useCallback(
    async (
      userId: number,
      defaultTeamUuid: string,
      organizations: any,
      tokenV2: string,
    ) => {
      try {
        v2.setAuthorizationHeader(tokenV2);

        // Store fake org fake folder
        let foldersToDirectoriesMap: { [k: string]: number } = {
          [generateUUID(getFakeOrgFakeFolderSeq(userId))]: 0,
        };

        const { payload: legacyFakeOrgFolders } = await v2.folders(0);

        // Store fake org folders
        legacyFakeOrgFolders?.forEach(({ id }: { id: number }) => {
          const sequence = getFakeOrgFolderSeq(userId, id);

          const directoryUUID = generateUUID(sequence);

          foldersToDirectoriesMap[directoryUUID] = id;
        });

        const orgsToTeamsMap: {
          [t: string]: { id: number; folders: { [d: string]: number } };
        } = {
          [defaultTeamUuid]: {
            id: 0,
            folders: foldersToDirectoriesMap,
          },
        };

        const orgIds = organizations?.map((item: { id: number }) => item?.id);

        orgIds?.forEach(async (org: number) => {
          const { payload: legacyFolders } = await v2.folders(org);

          const teamUUID = fp.compose(generateUUID, getOrgSeq)(org);

          // Store org fake folder
          foldersToDirectoriesMap = {
            [generateUUID(getOrgFakeFolderSeq(org))]: 0,
          };

          // store org folders
          legacyFolders?.forEach(({ id }: { id: number }) => {
            const sequence = getOrgFolderSeq(org, id);

            const directoryUUID = generateUUID(sequence);

            foldersToDirectoriesMap[directoryUUID] = id;
          });

          orgsToTeamsMap[teamUUID] = {
            id: org,
            folders: foldersToDirectoriesMap,
          };
        });
        setOrgsToTeamsMap(orgsToTeamsMap);
      } catch (e) {
        console.error(e);
      }
    },
    [setOrgsToTeamsMap, v2],
  );

  return fn;
};
